import axios from 'axios'
import { getToken, clearToken } from '@/helpers'
import isUserAnAdmin from './helpers'
import router from '../../../router'

import {
  uaaLogin,
  uaaFetchStatus,
  uaaLogout,
  uaaPostMFACode,
  uaaResendMFACode,
  uaaFetchAuthProviders,
  fetchUserId,
  fetchUserIdForImpersonation,
} from '../../../models/auth'

const login = async (context, userFormData) => {
  const { commit, dispatch } = context
  try {
    const confirmContentIdRoute = `https://${
      process.env.VUE_APP_MEMBERS_URL
    }/auth/verify?email_address=${userFormData.email.toLowerCase().trim()}`
    await axios
      .get(confirmContentIdRoute)
      .then(data => data)
      .catch(error => error)
    const user = await uaaLogin(userFormData)
    const memberId = user.customer_id
    commit('AUTH_MEMBER_ID_UPDATED', memberId)
    if (user.content_api_id) {
      commit('AUTH_USER_ID_UPDATED', user.content_api_id)
    } else {
      await dispatch('getUserId', memberId)
    }
    const isAdmin = isUserAnAdmin(user)
    commit('AUTH_IS_ADMIN_UPDATED', isAdmin)
    const { id: uaaId, email, account_id, team_ids } = user
    commit('AUTH_USER_PROFILE_UPDATED', {
      uaaId,
      email,
      account_id,
      team_ids,
      ...user.profile.data,
    })
    const { avatar } = user.profile.data
    commit('user/USER_AVATAR_UPDATED', avatar, { root: true })
    const { mfa_required: isTwoFactorRequired } = user.tokens.data
    if (isTwoFactorRequired) {
      commit('AUTH_IS_TWO_FACTOR_REQUIRED_UPDATED', isTwoFactorRequired)
    } else {
      commit('AUTH_IS_AUTHENTICATED_UPDATED', true)
    }
  } catch (errorObj) {
    const { error } = errorObj
    const message =
      error && error.message ? error.message : 'Something went wrong'
    context.dispatch('app/setError', message, { root: true })
  }
}

const tryAutoLogin = async ({ commit, dispatch }) => {
  const token = await getToken()
  if (token) {
    try {
      const user = await uaaFetchStatus()
      if (user) {
        const memberId = user.customer_id
        commit('AUTH_MEMBER_ID_UPDATED', memberId)
        if (user.content_api_id) {
          commit('AUTH_USER_ID_UPDATED', user.content_api_id)
        } else {
          await dispatch('getUserId', memberId)
        }
        const isAdmin = isUserAnAdmin(user)
        commit('AUTH_IS_ADMIN_UPDATED', isAdmin)
        const { id: uaaId, email, account_id, team_ids } = user
        commit('AUTH_USER_PROFILE_UPDATED', {
          uaaId,
          email,
          account_id,
          team_ids,
          ...user.profile.data,
        })
        const { avatar } = user.profile.data
        commit('user/USER_AVATAR_UPDATED', avatar, { root: true })
        commit('AUTH_IS_AUTHENTICATED_UPDATED', true)
      }
    } catch (e) {
      clearToken()
      commit('AUTH_MEMBER_ID_UPDATED', '')
      commit('AUTH_USER_PROFILE_UPDATED', '')
      commit('AUTH_USER_ID_UPDATED', null)
      commit('AUTH_IS_AUTHENTICATED_UPDATED', false)
    }
  } else {
    clearToken()
    commit('AUTH_MEMBER_ID_UPDATED', '')
    commit('AUTH_USER_PROFILE_UPDATED', '')
    commit('AUTH_USER_ID_UPDATED', null)
    commit('AUTH_IS_AUTHENTICATED_UPDATED', false)
  }
}

const federatedLogin = async ({ commit, dispatch }) => {
  const token = await getToken()
  if (token) {
    try {
      const user = await uaaFetchStatus()
      if (user) {
        const memberId = user.customer_id
        commit('AUTH_MEMBER_ID_UPDATED', memberId)
        if (user.content_api_id) {
          commit('AUTH_USER_ID_UPDATED', user.content_api_id)
        } else {
          await dispatch('getUserId', memberId)
        }
        const isAdmin = isUserAnAdmin(user)
        commit('AUTH_IS_ADMIN_UPDATED', isAdmin)
        const { id: uaaId, email, account_id, team_ids } = user
        commit('AUTH_USER_PROFILE_UPDATED', {
          uaaId,
          email,
          account_id,
          team_ids,
          ...user.profile.data,
        })
        const { avatar } = user.profile.data
        commit('user/USER_AVATAR_UPDATED', avatar, { root: true })
        commit('AUTH_IS_AUTHENTICATED_UPDATED', true)
      }
    } catch (errorObj) {
      const { error } = errorObj
      if (error && error.code && error.code === 'MFAEXCEPTION-REQUIRED') {
        commit('AUTH_IS_TWO_FACTOR_REQUIRED_UPDATED', true)
      } else {
        clearToken()
        commit('AUTH_MEMBER_ID_UPDATED', '')
        commit('AUTH_USER_PROFILE_UPDATED', '')
        commit('AUTH_USER_ID_UPDATED', null)
        commit('AUTH_IS_AUTHENTICATED_UPDATED', false)
        router.push({
          name: 'login',
          params: {
            error:
              'We were unable to log you in with that method. Please try again.',
          },
        })
      }
    }
  } else {
    clearToken()
    commit('AUTH_MEMBER_ID_UPDATED', '')
    commit('AUTH_USER_PROFILE_UPDATED', '')
    commit('AUTH_USER_ID_UPDATED', null)
    commit('AUTH_IS_AUTHENTICATED_UPDATED', false)
  }
}

const logout = async ({ commit }) => {
  try {
    await uaaLogout()
  } catch (error) {
    console.log(error)
  }
  clearToken()
  commit('AUTH_USER_PROFILE_UPDATED', '')
  commit('AUTH_IS_IMPERSONATING_UPDATED', false)
  commit('AUTH_CURRENT_IMPERSONATED_USER_UPDATED', null)
  commit('AUTH_IS_ADMIN_UPDATED', null)
  commit('AUTH_USER_ID_UPDATED', null)
  commit('AUTH_IS_AUTHENTICATED_UPDATED', false)
}

const submitMFACode = async (context, code) => {
  try {
    const response = await uaaPostMFACode(code)
    context.commit('AUTH_IS_TWO_FACTOR_REQUIRED_UPDATED', false)
    const {
      id: uaaId,
      customer_id: memberId,
      content_api_id: contentId,
      email,
      account_id,
      team_ids,
      profile,
    } = response
    context.commit('AUTH_IS_ADMIN_UPDATED', isUserAnAdmin(response))
    context.commit('AUTH_MEMBER_ID_UPDATED', memberId)
    context.commit('AUTH_USER_ID_UPDATED', contentId)

    context.commit('AUTH_USER_PROFILE_UPDATED', {
      uaaId,
      email,
      account_id,
      team_ids,
      ...profile.data,
    })
    const { avatar } = response.profile.data
    context.commit('user/USER_AVATAR_UPDATED', avatar, { root: true })
    context.commit('AUTH_IS_AUTHENTICATED_UPDATED', true)
  } catch (errorObj) {
    const { error } = errorObj
    const message =
      error && error.message ? error.message : 'Something went wrong'
    context.dispatch('app/setError', message, { root: true })
  }
}

const resendMFACode = async context => {
  try {
    await uaaResendMFACode()
  } catch (errorObj) {
    const { error } = errorObj
    const message =
      error && error.message ? error.message : 'Something went wrong'
    context.dispatch('app/setError', message, { root: true })
  }
}

const getAuthProviders = async context => {
  try {
    const response = await uaaFetchAuthProviders()
    context.commit('FEDERATED_AUTH_PROVIDERS_UPDATED', response)
  } catch (errorObj) {
    const { error } = errorObj
    const message =
      error && error.message ? error.message : 'Something went wrong'
    context.dispatch('app/setError', message, { root: true })
  }
}

const setImpersonateMode = (context, payload) => {
  context.commit('AUTH_IS_IMPERSONATING_UPDATED', payload.impersonate)
  context.commit('AUTH_CURRENT_IMPERSONATED_USER_UPDATED', payload.username)
}

const getUserIdForImpersonation = async (context, email) => {
  const response = await fetchUserIdForImpersonation(email)
  context.commit('AUTH_USER_ID_UPDATED', response)
}

const getUserId = async (context, id) => {
  try {
    const userId = await fetchUserId(id)
    context.commit('AUTH_USER_ID_UPDATED', userId)
  } catch (error) {
    console.log(error)
  }
}

const setIsTwoFactorRequired = (context, isTwoFactorRequired) => {
  context.commit('AUTH_IS_TWO_FACTOR_REQUIRED_UPDATED', isTwoFactorRequired)
}

const setIsAuthLoading = ({ commit }, isLoading) => {
  commit('AUTH_IS_AUTH_LOADING_UPDATED', isLoading)
}

const setRedirectUrl = (context, redirectUrl) => {
  context.commit('AUTH_REDIRECT_URL_UPDATED', redirectUrl)
}

export default {
  login,
  logout,
  tryAutoLogin,
  federatedLogin,
  submitMFACode,
  resendMFACode,
  getAuthProviders,
  setImpersonateMode,
  getUserIdForImpersonation,
  getUserId,
  setIsTwoFactorRequired,
  setIsAuthLoading,
  setRedirectUrl,
}
